import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { formatPrice, BRAND_COLORS } from "@/lib/menuData";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription 
} from "@/components/ui/sheet";
import { 
  Clock, Package, ChevronRight, RefreshCw, User, LogOut, History, MessageCircle 
} from "lucide-react";
import OrderChatPanel from "@/components/OrderChatPanel";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_foodapp-24/artifacts/qn823x2x_PHOTO-2026-02-06-16-32-00.jpg";

function formatDate(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  
  return date.toLocaleDateString("en-US", { 
    month: "short", 
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

function getStatusBadge(status) {
  const statusConfig = {
    new: { label: "Pending", color: "bg-yellow-100 text-yellow-700" },
    pending: { label: "Pending", color: "bg-yellow-100 text-yellow-700" },
    preparing: { label: "Preparing", color: "bg-blue-100 text-blue-700" },
    accepted: { label: "Preparing", color: "bg-blue-100 text-blue-700" },
    ready: { label: "Ready", color: "bg-green-100 text-green-700" },
    picked_up: { label: "Picked Up", color: "bg-emerald-100 text-emerald-700" },
    completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700" },
    declined: { label: "Declined", color: "bg-red-100 text-red-600" },
    deleted: { label: "Cancelled", color: "bg-red-100 text-red-600" }
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  return <Badge className={`${config.color} text-[10px]`}>{config.label}</Badge>;
}

export default function OrderHistoryDrawer({ open, onClose, onReorder }) {
  const { customer, isAuthenticated, getOrderHistory, logout } = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOrderId, setChatOrderId] = useState(null);

  useEffect(() => {
    if (open && isAuthenticated) {
      loadOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const orderHistory = await getOrderHistory();
      setOrders(orderHistory);
    } catch (e) {
      console.error("Failed to load order history:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = (order) => {
    if (onReorder) {
      onReorder(order.items);
      onClose();
    }
  };

  const handleLogout = async () => {
    await logout();
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex flex-col w-full sm:max-w-md p-0" style={{ backgroundColor: BRAND_COLORS.sandWhite }}>
        <SheetHeader className="px-6 pt-6 pb-4" style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
          <SheetTitle style={{ fontFamily: "Montserrat" }} className="flex items-center gap-2 text-white">
            <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-full object-cover" />
            My Account
          </SheetTitle>
          <SheetDescription className="text-white/80">
            {customer?.name || "Guest"}
          </SheetDescription>
        </SheetHeader>

        {/* Customer Info Card */}
        {isAuthenticated && customer && (
          <div className="px-6 py-4 bg-white border-b">
            <div className="flex items-center gap-3">
              {customer.picture ? (
                <img src={customer.picture} alt="" className="h-12 w-12 rounded-full" />
              ) : (
                <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.tropicalAqua }}>
                  <User className="h-6 w-6 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate" style={{ color: BRAND_COLORS.darkSlate }}>{customer.name}</p>
                <p className="text-sm truncate" style={{ color: BRAND_COLORS.darkSlate + "99" }}>{customer.email}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleLogout}
                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Order History Section */}
        <div className="px-6 pt-4 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4" style={{ color: BRAND_COLORS.deepTeal }} />
            <span className="text-sm font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>Order History</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadOrders}
            disabled={loading}
            className="h-8 px-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} style={{ color: BRAND_COLORS.tropicalAqua }} />
          </Button>
        </div>

        <ScrollArea className="flex-1 px-6">
          {loading ? (
            <div className="py-12 text-center">
              <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin" style={{ color: BRAND_COLORS.tropicalAqua }} />
              <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "66" }}>Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto mb-3" style={{ color: BRAND_COLORS.darkSlate + "30" }} />
              <p className="text-sm" style={{ color: BRAND_COLORS.darkSlate + "66" }}>No orders yet</p>
              <p className="text-xs mt-1" style={{ color: BRAND_COLORS.darkSlate + "50" }}>Your order history will appear here</p>
            </div>
          ) : (
            <div className="space-y-3 pb-4">
              {orders.map((order) => (
                <div 
                  key={order.id} 
                  data-testid={`order-history-${order.id}`}
                  className="bg-white rounded-xl p-4 shadow-sm border border-slate-100"
                >
                  {/* Order Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-semibold" style={{ color: BRAND_COLORS.darkSlate }}>
                        #{order.id?.slice(0, 8).toUpperCase()}
                      </span>
                      {getStatusBadge(order.status)}
                    </div>
                    <div className="flex items-center gap-1 text-xs" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
                      <Clock className="h-3 w-3" />
                      {formatDate(order.created_at)}
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="space-y-1 mb-3">
                    {order.items?.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span style={{ color: BRAND_COLORS.darkSlate + "99" }}>
                          {item.quantity}x {item.name}
                        </span>
                        <span style={{ color: BRAND_COLORS.darkSlate }}>{formatPrice(item.price * item.quantity)}</span>
                      </div>
                    ))}
                    {order.items?.length > 3 && (
                      <p className="text-xs" style={{ color: BRAND_COLORS.darkSlate + "66" }}>
                        +{order.items.length - 3} more items
                      </p>
                    )}
                  </div>

                  {/* Order Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="font-bold" style={{ color: BRAND_COLORS.sunsetOrange }}>
                      {formatPrice(order.total)}
                    </span>
                    <div className="flex gap-2 flex-wrap justify-end">
                      {/* Chat with Store — for orders where the chat is still open (not picked_up/declined) */}
                      {!["picked_up", "declined", "deleted"].includes(order.status) && (
                        <Button
                          data-testid={`profile-chat-${order.id}`}
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs rounded-lg"
                          style={{ borderColor: BRAND_COLORS.sunsetOrange, color: BRAND_COLORS.sunsetOrange }}
                          onClick={() => setChatOrderId(order.id)}
                        >
                          <MessageCircle className="h-3 w-3 mr-1" />
                          Chat
                        </Button>
                      )}
                      {/* Track Order - only for active orders */}
                      {["new", "pending", "preparing", "accepted"].includes(order.status) && (
                        <Link to={`/order/status/${order.id}`}>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="h-8 text-xs rounded-lg"
                            style={{ borderColor: BRAND_COLORS.tropicalAqua, color: BRAND_COLORS.deepTeal }}
                          >
                            Track
                          </Button>
                        </Link>
                      )}
                      {/* Reorder Button */}
                      <Button 
                        size="sm"
                        className="h-8 text-xs rounded-lg text-white"
                        style={{ backgroundColor: BRAND_COLORS.sunsetOrange }}
                        onClick={() => handleReorder(order)}
                        data-testid={`reorder-${order.id}`}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Reorder
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Bottom Action */}
        <div className="px-6 py-4 bg-white border-t">
          <Link to="/hub">
            <Button 
              className="w-full h-12 rounded-xl font-semibold text-white"
              style={{ backgroundColor: BRAND_COLORS.deepTeal }}
              onClick={onClose}
            >
              Start New Order
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </SheetContent>

      {/* Chat with Store — nested sheet (opens on top of the profile drawer) */}
      <Sheet open={!!chatOrderId} onOpenChange={(o) => !o && setChatOrderId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b bg-white shrink-0">
            <SheetTitle className="text-base flex items-center gap-2">
              <MessageCircle className="h-4 w-4" style={{ color: BRAND_COLORS.sunsetOrange }} />
              Chat with the Store
            </SheetTitle>
            <SheetDescription className="text-[11px] text-slate-500">
              Order #{chatOrderId ? chatOrderId.slice(0, 8) : ""}
            </SheetDescription>
          </SheetHeader>
          <div className="flex-1 min-h-0">
            {chatOrderId && (
              <OrderChatPanel
                orderId={chatOrderId}
                viewer="customer"
                senderName={customer?.name}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </Sheet>
  );
}
